import { formatYYMMDDandTime } from "./format";

const LEGACY_ORDER_TYPE_LABELS: Record<string, string> = {
  basic: "Mapping",
  thermal: "Thermal Mapping",
  inventory: "Mapping & Inventory Measurement",
  custom: "Custom Request",
};

export function normalizeForCompare(value?: string | null): string {
  return (value ?? "").trim().toLowerCase();
}

type OrderTypeLabelInput = Pick<
  { type: string; order_type_name?: string | null },
  "type" | "order_type_name"
>;

export function getOrderTypeLabel(order: OrderTypeLabelInput): string | null {
  if (order.order_type_name) return order.order_type_name;

  if (LEGACY_ORDER_TYPE_LABELS[order.type]) {
    return LEGACY_ORDER_TYPE_LABELS[order.type];
  }

  return null;
}

export type OrderRow = {
  id: number;
  typeLabel: string;
  createdBy: string;
  submitted: string;
  completed: string;
};

type OrderRowInput = {
  id: number;
  type: string;
  order_type_name?: string | null;
  created_by_name?: string | null;
  submitted: string | null;
  completed: string | null;
};

export function buildOrderRows(orders: OrderRowInput[]): OrderRow[] {
  return [...orders]
    .sort((a, b) => a.id - b.id)
    .map((order) => ({
      id: order.id,
      typeLabel: getOrderTypeLabel(order) ?? "-",
      createdBy: order.created_by_name || "-",
      submitted: order.submitted ? formatYYMMDDandTime(order.submitted) : "-",
      completed: order.completed ? formatYYMMDDandTime(order.completed) : "-",
    }));
}

export function buildOrderTypeLabelsBySurveyId(
  orders: Array<{ survey_id?: number | null } & OrderTypeLabelInput>
): Map<number, string[]> {
  const labelsBySurveyId = new Map<number, string[]>();

  orders.forEach((order) => {
    const surveyId = order?.survey_id;
    if (typeof surveyId !== "number") return;

    const label = getOrderTypeLabel(order);
    if (!label) return;

    const existing = labelsBySurveyId.get(surveyId);
    if (existing) existing.push(label);
    else labelsBySurveyId.set(surveyId, [label]);
  });

  return labelsBySurveyId;
}

export function collectUniqueOrderTypeLabels(
  orders: Array<{ survey_id?: number | null } & OrderTypeLabelInput>
): string[] {
  const representativeByKey = new Map<string, string>();

  buildOrderTypeLabelsBySurveyId(orders).forEach((labels) => {
    labels.forEach((label) => {
      const key = normalizeForCompare(label);
      if (!representativeByKey.has(key)) representativeByKey.set(key, label);
    });
  });

  return Array.from(representativeByKey.values()).sort((a, b) =>
    a.localeCompare(b)
  );
}

export function resolveSelectedOrderTypes(
  selectedValues: string[] | null | undefined,
  availableOptions: Array<{ value: string; label: string }> | null | undefined
): Array<{ value: string; label: string }> {
  if (!selectedValues) return [];
  const options = availableOptions ?? [];
  return selectedValues.map((value) => {
    const exact = options.find((o) => o.value === value);
    if (exact) return exact;
    const normalized = options.find(
      (o) => normalizeForCompare(o.value) === normalizeForCompare(value)
    );
    return { value, label: normalized ? normalized.label : value };
  });
}

export function formatOrderTypeLabelsForDisplay(labels?: string[] | null): string | null {
  if (!labels || labels.length === 0) return null;

  // Deterministic + stable across sort/display/search even if multiple orders exist.
  const uniqueSorted = Array.from(new Set(labels)).sort((a, b) => a.localeCompare(b));
  return uniqueSorted.join(", ");
}

